import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CountryFlag from './CountryFlag';
import { useLanguage } from '@/i18n/useLanguage';
import type { LangMode } from '@/i18n/useLanguage';

const LANGUAGES: { code: LangMode; iso: string; label: string }[] = [
  { code: 'en', iso: 'GB', label: 'English' },
  { code: 'zh', iso: 'CN', label: '中文' },
  { code: 'km', iso: 'KH', label: 'ភាសាខ្មែរ' },
];

// Round globe button that opens a flag list of the site's languages.
export default function LanguageMenu() {
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        aria-label={t('header.language')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-0.5 cursor-pointer group"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 group-hover:bg-neutral-200 transition-colors">
          <i className="ri-global-line text-xl leading-none text-neutral-900" aria-hidden="true" />
        </span>
        <i className={`ri-arrow-down-s-fill text-sm text-neutral-600 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-44 rounded-xl border border-neutral-200 bg-white p-1.5 shadow-xl z-50">
          {LANGUAGES.map((option) => (
            <button
              key={option.code}
              type="button"
              role="menuitemradio"
              aria-checked={lang === option.code}
              onClick={() => { setLang(option.code); setOpen(false); }}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-left cursor-pointer transition-colors ${
                lang === option.code ? 'bg-primary-50 text-primary-700 font-medium' : 'text-neutral-900 hover:bg-neutral-50'
              }`}
            >
              <CountryFlag iso={option.iso} className="w-6 h-4 shrink-0 rounded-[2px]" />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
